/** FlowConfigTab (draft edits + metrics). */
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { LoadingSpinner } from '@/components/ui';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { formatDateDMY } from '@/features/smswiser-campaigns/utils/dateFormat';
import {
  DateRangePresetDropdown,
  getRangeFromPreset,
  getStatsDateRangeLabel,
} from '@/components/date-range';
import type { StatsDateRangePreset } from '@/components/date-range';
import type { Flow } from '@/hooks';
import { NO_GROUPS, NO_PATHS } from '@/features/flows/detail/utils/flow-fallbacks';
import { useFlowSaveHandler } from '@/features/flows/detail/hooks/draft-save/useFlowSaveHandler';

import { PreviewIndexProvider } from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import { flowWritesAllowed, optimizerProviderOf } from '@/features/campaigns/tracker/campaign-tracker-access';
import { resolveInitialPreset, resolveInitialRange } from '@/features/flows/detail/utils/flow-metrics-range.util';

import FlowLandersOffersPanel from '../panels/flow-landers-offers';
import FlowDraftActionsBar from '../common/FlowDraftActionsBar';
import FlowPathGroupChips from '../common/FlowPathGroupChips';
import { FlowPathsPanel } from '../panels';
import { useFlowDraft } from '../../hooks/useFlowDraft';
import { useFlowLanderOfferSelection } from '../../hooks/useFlowLanderOfferSelection';
import { useFlowItemMetricsCube } from '../../hooks/useFlowItemMetricsCube';
import {
  FlowOverlaySortControls,
  FlowOverlaySortProvider,
} from '../../overlay-sort';
import { useFlowOptimizerOverlay } from '../../hooks/useFlowOptimizerOverlay';
import { useFlowPathGroups } from '../../hooks/useFlowPathGroups';
import { buildOffersForSelectedLander } from '../../utils/flow-offers-for-lander.util';

import type { CampaignProvider } from '@/hooks/voluum/useVoluum';

export interface FlowConfigTabProps {
  flowId: string;
  campaignId?: string;
  /** Defaults to 'voluum'. Edits follow `flowWrites`, the optimizer overlay `flowOptimizer` (registry). */
  provider?: CampaignProvider;
  /** Initial preset for the metrics-range picker. The user can change it locally afterwards. */
  preferredDateRangePreset?: string;
  customFrom?: string;
  customTo?: string;
  /**
   * Notified whenever the local metrics range changes, so a parent (campaign detail)
   * can keep the header KPI totals in sync with the paths/landers/offers range.
   */
  onActiveRangeChange?: (range: { preset: string; from?: string; to?: string }) => void;
  isLoading: boolean;
  flow?: Flow;
  /** Nested inside campaign flow Card — only a top divider, no second “card” frame. */
  pathsGridInCard?: boolean;
  /** Dev-only: bypass enabled/kill-switch checks and load optimizer preview rows. */
  simulateOptimizerOverlay?: boolean;
}

export const FlowConfigTab: FC<FlowConfigTabProps> = ({
  flowId,
  flow,
  isLoading,
  campaignId,
  provider = 'voluum',
  preferredDateRangePreset,
  customFrom,
  customTo,
  onActiveRangeChange,
  pathsGridInCard = false,
  simulateOptimizerOverlay = false,
}) => {
  const { pathsAll, groupOptions, selectedGroupId, setSelectedGroupId } = useFlowPathGroups({
    flowId,
    defaultPaths: flow?.defaultPaths ?? NO_PATHS,
    pathGroups: flow?.pathGroups ?? NO_GROUPS,
  });

  const [expandedPathId, setExpandedPathId] = useState<string | null>(null);
  const [expandedLanderId, setExpandedLanderId] = useState<string | null>(null);
  const [expandedOfferId, setExpandedOfferId] = useState<string | null>(null);

  const [dateRange, setDateRange] = useState<{ from: string; to: string }>(() =>
    resolveInitialRange(preferredDateRangePreset, customFrom, customTo),
  );
  // Track the chosen preset explicitly. Rolling presets (last-24-hours) resolve to a
  // {from,to} identical to "today", so reverse-deriving the preset from the dates would
  // collapse last-24h → today. Custom date inputs set this to 'custom'.
  const [selectedDateRangePreset, setSelectedDateRangePreset] =
    useState<StatsDateRangePreset>(() =>
      resolveInitialPreset(preferredDateRangePreset, customFrom, customTo),
    );

  const dateRangeLabel = useMemo(
    () =>
      selectedDateRangePreset === 'custom'
        ? `${formatDateDMY(dateRange.from)} → ${formatDateDMY(dateRange.to)}`
        : getStatsDateRangeLabel(selectedDateRangePreset),
    [selectedDateRangePreset, dateRange.from, dateRange.to],
  );

  // Keep the parent (campaign header KPI totals) in sync with this picker. Ref-based
  // so a parent passing an inline callback can't retrigger the effect every render.
  const onActiveRangeChangeRef = useRef(onActiveRangeChange);
  onActiveRangeChangeRef.current = onActiveRangeChange;
  useEffect(() => {
    onActiveRangeChangeRef.current?.({
      preset: selectedDateRangePreset,
      from: dateRange.from,
      to: dateRange.to,
    });
  }, [selectedDateRangePreset, dateRange.from, dateRange.to]);

  const {
    draftPaths,
    selectedPath,
    selectedPathId,
    setSelectedPathId,
    dirtyCount,
    isSavingDraft,
    resetDraft,
    saveDraft,
    togglePathActive,
    toggleItemActive,
  } = useFlowDraft({
    flowId,
    campaignId,
    provider,
    flowName: flow?.name ?? '',
    pathsRaw: pathsAll,
    pathGroupsRaw: flow?.pathGroups ?? NO_GROUPS,
  });

  const currentPathId = String(selectedPath?.id ?? '');

  useEffect(() => {
    const inGroup = draftPaths.filter((p) => String(p.groupId ?? 'default') === selectedGroupId);
    if (inGroup.length <= 0) return;
    if (inGroup.some((p) => p.id === selectedPathId)) return;
    setSelectedPathId(inGroup.find((p) => p.active)?.id ?? inGroup[0].id);
  }, [draftPaths, selectedGroupId, selectedPathId, setSelectedPathId]);

  const landersForPanel = useMemo(
    () => (selectedPath?.landers ?? []).map((l) => ({ id: l.id, name: l.name, weight: l.weight, originalWeight: l.originalWeight })),
    [selectedPath?.landers],
  );
  const offersAllForPanel = useMemo(
    () => (selectedPath?.offers ?? []).map((o) => ({ id: o.id, name: o.name, weight: o.weight, originalWeight: o.originalWeight })),
    [selectedPath?.offers],
  );

  const { selectedLanderId, setSelectedLanderId, selectedLanderName } = useFlowLanderOfferSelection({
    landers: landersForPanel,
  });

  const cubeQuery = useFlowItemMetricsCube({
    flowId,
    campaignId,
    provider,
    dateRangePreset: selectedDateRangePreset,
    customFrom: selectedDateRangePreset === 'custom' ? dateRange.from : undefined,
    customTo: selectedDateRangePreset === 'custom' ? dateRange.to : undefined,
  });

  const metricsLoading = cubeQuery.isLoading;
  const metricsError = cubeQuery.error;
  const pathMetricsById = cubeQuery.data?.pathMetricsById;
  const landerMetricsById = cubeQuery.data?.landerMetricsById;
  const offerIdsByLanderId = cubeQuery.data?.offerIdsByLanderId;
  const offerMetricsByLanderId = cubeQuery.data?.offerMetricsByLanderId;
  // Per-path maps (Binom): a lander/offer shared across paths has different stats
  // per path; the panel prefers these over the flat maps (undefined for Voluum).
  const landerMetricsByPathId = cubeQuery.data?.landerMetricsByPathId;
  const offerMetricsByPathId = cubeQuery.data?.offerMetricsByPathId;
  // pathId → landerId → offerId: an offer's stats within a specific lander.
  const offerMetricsByPathLanderId = cubeQuery.data?.offerMetricsByPathLanderId;

  const offerMetricsForSelectedLander = useMemo(() => {
    if (!selectedLanderId) return {};
    return offerMetricsByLanderId?.[selectedLanderId] ?? {};
  }, [offerMetricsByLanderId, selectedLanderId]);

  const offersForSelectedLander = useMemo(
    () => buildOffersForSelectedLander({ selectedLanderId, offers: offersAllForPanel, offerIdsByLanderId }),
    [offerIdsByLanderId, offersAllForPanel, selectedLanderId],
  );

  const optimizer = optimizerProviderOf(provider);
  const writable = flowWritesAllowed(provider);
  const onSave = useFlowSaveHandler(provider, saveDraft);
  const overlay = useFlowOptimizerOverlay({
    flowId,
    campaignId: optimizer ? campaignId : undefined, // no optimizer → no config / preview read
    provider: optimizer ?? undefined,
    simulate: simulateOptimizerOverlay,
  });

  const pathList = useMemo(() => {
    return draftPaths.filter((p) => String(p.groupId ?? 'default') === selectedGroupId).map((p) => ({
      id: p.id,
      name: p.name,
      weight: p.weight,
      active: p.active,
      originalWeight: p.originalWeight,
      originalActive: p.originalActive,
    }));
  }, [draftPaths, selectedGroupId]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const pathsGrid = (
    <div
      className={cn(
        'grid min-h-0 grid-cols-12 divide-y divide-border lg:divide-x lg:divide-y-0',
        pathsGridInCard
          ? 'mt-2 border-t border-border/40 pt-1'
          : 'overflow-hidden rounded-lg border border-border bg-card shadow-sm',
      )}
    >
      <FlowPathsPanel
        paths={pathList} selectedPathId={selectedPathId} expandedPathId={expandedPathId} onSelectPath={setSelectedPathId}
        onToggleExpand={(pathId) => setExpandedPathId((current) => (current === pathId ? null : pathId))}
        onToggleActive={writable ? togglePathActive : undefined} pathMetricsById={pathMetricsById} metricsRangeLabel={dateRangeLabel}
        isMutating={isSavingDraft}
      />

      <FlowLandersOffersPanel
        selectedPathName={selectedPath?.name} selectedPathId={currentPathId} selectedLanderId={selectedLanderId ?? undefined}
        selectedLanderName={selectedLanderName} onSelectLander={setSelectedLanderId} landers={landersForPanel} offers={offersForSelectedLander}
        expandedLanderId={expandedLanderId} expandedOfferId={expandedOfferId}
        onToggleExpandLander={(landerId) => setExpandedLanderId((c) => (c === landerId ? null : landerId))}
        onToggleExpandOffer={(offerId) => setExpandedOfferId((c) => (c === offerId ? null : offerId))}
        onToggleLanderActive={writable ? (landerId, active) => currentPathId && toggleItemActive({ pathId: currentPathId, type: 'landers', itemId: landerId, nextActive: active }) : undefined}
        onToggleOfferActive={writable ? (offerId, active) => currentPathId && toggleItemActive({ pathId: currentPathId, type: 'offers', itemId: offerId, nextActive: active }) : undefined}
        landerMetricsById={landerMetricsById} offerMetricsById={offerMetricsForSelectedLander}
        landerMetricsByPathId={landerMetricsByPathId} offerMetricsByPathId={offerMetricsByPathId}
        offerMetricsByPathLanderId={offerMetricsByPathLanderId}
        metricsRangeLabel={dateRangeLabel} isMutating={isSavingDraft}
      />
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 px-3 sm:px-4">
        <span className="text-xs font-medium text-muted-foreground">Metrics range</span>
        <DateRangePresetDropdown
          fromDate={dateRange.from}
          toDate={dateRange.to}
          activePreset={selectedDateRangePreset}
          onPresetChange={(p) => {
            setSelectedDateRangePreset(p);
            const r = getRangeFromPreset(p);
            if (r) setDateRange(r);
          }}
          onFromChange={(from) => {
            setSelectedDateRangePreset('custom');
            setDateRange((s) => ({ ...s, from }));
          }}
          onToChange={(to) => {
            setSelectedDateRangePreset('custom');
            setDateRange((s) => ({ ...s, to }));
          }}
        />
      </div>
      <div className="px-3 sm:px-4">
        <FlowPathGroupChips options={groupOptions} selectedId={selectedGroupId} onSelect={setSelectedGroupId} />
      </div>

      {overlay.active ? (
        <PreviewIndexProvider
          items={overlay.items}
          countryKey={overlay.countryKey}
          campaignId={overlay.campaignId}
          provider={overlay.provider}
          tracker={optimizer ?? undefined}
        >
          <FlowOverlaySortProvider>
            <FlowOverlaySortControls />
            {pathsGrid}
          </FlowOverlaySortProvider>
        </PreviewIndexProvider>
      ) : (
        pathsGrid
      )}

      {writable ? (
        <div className="px-3 sm:px-4">
          <FlowDraftActionsBar
            dirtyCount={dirtyCount}
            saving={isSavingDraft}
            onReset={resetDraft}
            onSave={onSave}
          />
        </div>
      ) : null}
      {metricsLoading && (
        <div className="flex justify-center px-3 py-2 sm:px-4">
          <LoadingSpinner />
        </div>
      )}
      {metricsError && (
        <p className="px-3 text-sm text-destructive sm:px-4">
          Failed to load metrics: {getApiErrorMessage(metricsError, 'Unknown error')}
        </p>
      )}
    </div>
  );
};

export default FlowConfigTab;

